import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import { IconButton } from "../../../components/ui";
import LogoText from "../../../media/60njktpitzg8gano2erfouonjejsbaz5.png";
import userPlanPrint from "../../../media/userPlanPrint.svg";
import doctorPlanPrint from "../../../media/doctorPlanPrint.svg";
import "./PatientPlanPrint.css";
import "./PatientProfilePrint.css";
import PatientProfilePrintFormView from "./PatientProfilePrintFormView";

const LIFE_ANAMNESIS_DEFAULT = {
  general_condition: "",
  operations_injuries: "",
  medications: "",
};

const LIFE_ANAMNESIS_FIELD_LABELS = {
  general_condition: "anamnesis.life.generalCondition",
  operations_injuries: "anamnesis.life.operationsInjuries",
  medications: "anamnesis.life.medications",
};

const SOMATIC_DISEASES_DEFAULT = {
  chronic_diseases: "",
  endocrine_status: "",
};

const GYN_ANAMNESIS_DEFAULT = {
  menstruation_age: "",
  regularity: "",
  cycle_duration: "",
  pms: "",
  pregnancy_r: "",
  pregnancy_a: "",
  pregnancy_v: "",
  menstrual_symptoms: "",
  menopause_age: "",
  hormonal_drugs: "",
};

const ALLERGY_ANAMNESIS_KEYS = [
  "medications",
  "cosmetics",
  "food",
  "household",
  "plants",
  "sun",
  "cold",
];

const ALLERGY_ANAMNESIS_FIELD_LABELS = {
  medications: "anamnesis.allergy.medications",
  cosmetics: "anamnesis.allergy.cosmetics",
  food: "anamnesis.allergy.food",
  household: "anamnesis.allergy.household",
  plants: "anamnesis.allergy.plants",
  sun: "anamnesis.allergy.sun",
  cold: "anamnesis.allergy.cold",
};

const EPID_ANAMNESIS_KEYS = [
  "hepatitis",
  "hiv",
  "syphilis",
  "tuberculosis",
  "herpes",
  "other_infectious",
  "std",
];

const EPID_ANAMNESIS_FIELD_LABELS = {
  hepatitis: "anamnesis.epid.hepatitis",
  hiv: "anamnesis.epid.hiv",
  syphilis: "anamnesis.epid.syphilis",
  tuberculosis: "anamnesis.epid.tuberculosis",
  herpes: "anamnesis.epid.herpes",
  other_infectious: "anamnesis.epid.otherInfectious",
  std: "anamnesis.epid.std",
};

const YES_NO_ANAMNESIS_ITEM_DEFAULT = { answer: "", note: "" };

const NUTRITION_ANAMNESIS_DEFAULT = {
  meal_regime: "",
  meals_per_day: "",
  food_character: [],
  fluid_liters: "",
  smoking_alcohol: "",
};

const NUTRITION_FOOD_CHARACTER_KEYS = [
  "balanced",
  "protein",
  "vegetarian",
  "high_fat",
  "fast_food",
  "high_carb",
  "spicy",
  "smoked",
  "salty",
  "sweet",
  "soda",
];

const NUTRITION_FOOD_CHARACTER_LABELS = {
  balanced: "anamnesis.nutrition.food.balanced",
  protein: "anamnesis.nutrition.food.protein",
  vegetarian: "anamnesis.nutrition.food.vegetarian",
  high_fat: "anamnesis.nutrition.food.highFat",
  fast_food: "anamnesis.nutrition.food.fastFood",
  high_carb: "anamnesis.nutrition.food.highCarb",
  spicy: "anamnesis.nutrition.food.spicy",
  smoked: "anamnesis.nutrition.food.smoked",
  salty: "anamnesis.nutrition.food.salty",
  sweet: "anamnesis.nutrition.food.sweet",
  soda: "anamnesis.nutrition.food.soda",
};

const DAILY_ROUTINE_DEFAULT = {
  sleep_hours: "",
  sleep_character: [],
  awakening: [],
  awakening_time: "",
  physical_activity: "",
  activity_peak: "",
  sports: "",
  stress_level: "",
};

const DAILY_ROUTINE_SLEEP_CHARACTER_KEYS = ["calm", "restless", "dreams"];
const DAILY_ROUTINE_SLEEP_CHARACTER_LABELS = {
  calm: "anamnesis.routine.sleep.calm",
  restless: "anamnesis.routine.sleep.restless",
  dreams: "anamnesis.routine.sleep.dreams",
};

const DAILY_ROUTINE_AWAKENING_KEYS = ["full_rest", "fatigue", "spontaneous"];
const DAILY_ROUTINE_AWAKENING_LABELS = {
  full_rest: "anamnesis.routine.wake.fullRest",
  fatigue: "anamnesis.routine.wake.fatigue",
  spontaneous: "anamnesis.routine.wake.spontaneous",
};

const DAILY_ROUTINE_ACTIVITY_KEYS = ["very_low", "low", "medium", "high"];
const DAILY_ROUTINE_ACTIVITY_LABELS = {
  very_low: "anamnesis.routine.activity.veryLow",
  low: "anamnesis.routine.activity.low",
  medium: "anamnesis.routine.activity.medium",
  high: "anamnesis.routine.activity.high",
};

const ANTHROPOMETRY_DEFAULT = {
  weight_kg: "",
  height_cm: "",
  body_type: "",
  weight_fluctuation_3_5_years: "",
  weight_fluctuation_1_year: "",
  volume_redistribution: "",
};

const ANTHROPOMETRY_BODY_TYPE_KEYS = ["normosthenic", "hypersthenic", "asthenic"];
const ANTHROPOMETRY_BODY_TYPE_LABELS = {
  normosthenic: "anamnesis.anthropometry.bodyTypeOptions.normosthenic",
  hypersthenic: "anamnesis.anthropometry.bodyTypeOptions.hypersthenic",
  asthenic: "anamnesis.anthropometry.bodyTypeOptions.asthenic",
};

const ANTHROPOMETRY_REDISTRIBUTION_KEYS = ["android", "gynoid", "mixed"];
const ANTHROPOMETRY_REDISTRIBUTION_LABELS = {
  android: "anamnesis.anthropometry.redistributionOptions.android",
  gynoid: "anamnesis.anthropometry.redistributionOptions.gynoid",
  mixed: "anamnesis.anthropometry.redistributionOptions.mixed",
};

const DISEASE_ANAMNESIS_KEYS = [
  "doctor_comment",
  "cardiovascular_diseases",
  "cns_diseases",
  "respiratory_diseases",
  "digestive_diseases",
  "vision_impairments",
  "thyroid_disease",
  "permanent_therapy",
];

const DISEASE_ANAMNESIS_LABELS = {
  doctor_comment: "profile.fields.doctorComment",
  cardiovascular_diseases: "profile.fields.cardiovascularDiseases",
  cns_diseases: "profile.fields.cnsDiseases",
  respiratory_diseases: "profile.fields.respiratoryDiseases",
  digestive_diseases: "profile.fields.digestiveDiseases",
  vision_impairments: "profile.fields.visionImpairments",
  thyroid_disease: "profile.fields.thyroidDisease",
  permanent_therapy: "profile.fields.permanentTherapy",
};

const OLD_LIFE_ANAMNESIS_KEYS = [
  "blood_tests",
  "past_conditions",
  "allergies",
  "heredity",
  "bad_habits",
];

const OLD_LIFE_ANAMNESIS_LABELS = {
  blood_tests: "profile.fields.epidAnamnesis",
  past_conditions: "profile.fields.pastConditions",
  allergies: "profile.fields.allergies",
  heredity: "profile.fields.heredity",
  bad_habits: "profile.fields.badHabits",
};

const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return "—";
  const [year, month, day] = String(isoDate).split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}.${year}`;
};

const calcAgeYears = (isoDate) => {
  if (!isoDate || typeof isoDate !== "string") return null;
  const parts = isoDate.split("-").map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  const birth = new Date(y, m - 1, d);
  if (Number.isNaN(birth.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const [datePart] = String(dateString).split(" ");
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}.${year}`;
};

const formatAnyValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((v) => (v && typeof v === "object" ? v?.name ?? v?.title ?? JSON.stringify(v) : String(v)))
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  }
  if (typeof value === "object") {
    if ("name" in value && value?.name != null) return String(value.name);
    if ("title" in value && value?.title != null) return String(value.title);
    if ("value" in value && value?.value != null) return formatAnyValue(value.value);
    return JSON.stringify(value);
  }
  return String(value);
};

const parseJsonObject = (raw, fallback) => {
  if (!raw) return { ...fallback };
  if (typeof raw === "object" && !Array.isArray(raw)) return { ...fallback, ...raw };
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return { ...fallback };
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return { ...fallback, ...parsed };
      }
    } catch (_) {
      return { ...fallback };
    }
  }
  return { ...fallback };
};

const parseLifeAnamnesis = (raw) => {
  const parsed = parseJsonObject(raw, LIFE_ANAMNESIS_DEFAULT);
  return {
    general_condition: parsed.general_condition ?? "",
    operations_injuries: parsed.operations_injuries ?? "",
    medications: parsed.medications ?? "",
  };
};

const parseSomaticDiseases = (raw) => {
  const parsed = parseJsonObject(raw, SOMATIC_DISEASES_DEFAULT);
  return {
    chronic_diseases: parsed.chronic_diseases ?? "",
    endocrine_status: parsed.endocrine_status ?? "",
  };
};

const parseGynAnamnesis = (raw) => {
  const parsed = parseJsonObject(raw, GYN_ANAMNESIS_DEFAULT);
  return {
    menstruation_age: parsed.menstruation_age ?? "",
    regularity: parsed.regularity ?? "",
    cycle_duration: parsed.cycle_duration ?? "",
    pms: parsed.pms ?? "",
    pregnancy_r: parsed.pregnancy_r ?? "",
    pregnancy_a: parsed.pregnancy_a ?? "",
    pregnancy_v: parsed.pregnancy_v ?? "",
    menstrual_symptoms: parsed.menstrual_symptoms ?? "",
    menopause_age: parsed.menopause_age ?? "",
    hormonal_drugs: parsed.hormonal_drugs ?? "",
  };
};

const normalizeYesNoAnamnesisItem = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...YES_NO_ANAMNESIS_ITEM_DEFAULT };
  }
  const answer = raw.answer === "yes" || raw.answer === "no" ? raw.answer : "";
  return { answer, note: raw.note ?? "" };
};

const parseYesNoAnamnesis = (raw, keys) => {
  const empty = () =>
    Object.fromEntries(keys.map((k) => [k, { ...YES_NO_ANAMNESIS_ITEM_DEFAULT }]));
  if (!raw) return empty();
  let source = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return empty();
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return empty();
      source = parsed;
    } catch (_) {
      return empty();
    }
  }
  if (typeof source !== "object" || Array.isArray(source)) return empty();
  return Object.fromEntries(keys.map((k) => [k, normalizeYesNoAnamnesisItem(source[k])]));
};

const parseAllergyAnamnesis = (raw) => parseYesNoAnamnesis(raw, ALLERGY_ANAMNESIS_KEYS);
const parseEpidAnamnesis = (raw) => parseYesNoAnamnesis(raw, EPID_ANAMNESIS_KEYS);

const normalizeCheckboxKeys = (raw, allowedKeys) => {
  if (!Array.isArray(raw)) return [];
  return allowedKeys.filter((key) => raw.includes(key));
};

const parseNutritionAnamnesis = (raw) => {
  const empty = { ...NUTRITION_ANAMNESIS_DEFAULT, food_character: [] };
  if (!raw) return empty;
  let source = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return empty;
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return empty;
      source = parsed;
    } catch (_) {
      return empty;
    }
  }
  if (typeof source !== "object" || Array.isArray(source)) return empty;
  const mealRegime =
    source.meal_regime === "regular" || source.meal_regime === "irregular"
      ? source.meal_regime
      : "";
  const smokingAlcohol = Object.prototype.hasOwnProperty.call(source, "smoking_alcohol")
    ? (source.smoking_alcohol ?? "")
    : [source.smoking, source.alcohol]
        .map((v) => (v == null ? "" : String(v).trim()))
        .filter(Boolean)
        .join(", ");
  return {
    meal_regime: mealRegime,
    meals_per_day: source.meals_per_day ?? "",
    food_character: normalizeCheckboxKeys(source.food_character, NUTRITION_FOOD_CHARACTER_KEYS),
    fluid_liters: source.fluid_liters ?? "",
    smoking_alcohol: smokingAlcohol,
  };
};

const parseDailyRoutineAnamnesis = (raw) => {
  const empty = { ...DAILY_ROUTINE_DEFAULT, sleep_character: [], awakening: [] };
  if (!raw) return empty;
  let source = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return empty;
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return empty;
      source = parsed;
    } catch (_) {
      return empty;
    }
  }
  if (typeof source !== "object" || Array.isArray(source)) return empty;
  return {
    sleep_hours: source.sleep_hours ?? "",
    sleep_character: normalizeCheckboxKeys(
      source.sleep_character,
      DAILY_ROUTINE_SLEEP_CHARACTER_KEYS,
    ),
    awakening: normalizeCheckboxKeys(source.awakening, DAILY_ROUTINE_AWAKENING_KEYS),
    awakening_time: source.awakening_time ?? "",
    physical_activity: DAILY_ROUTINE_ACTIVITY_KEYS.includes(source.physical_activity)
      ? source.physical_activity
      : "",
    activity_peak: source.activity_peak ?? "",
    sports: source.sports === "yes" || source.sports === "no" ? source.sports : "",
    stress_level: source.stress_level ?? "",
  };
};

const calcBmi = (weightKg, heightCm) => {
  const weight = Number.parseFloat(String(weightKg ?? "").replace(",", "."));
  const height = Number.parseFloat(String(heightCm ?? "").replace(",", "."));
  if (!Number.isFinite(weight) || !Number.isFinite(height) || weight <= 0 || height <= 0) {
    return "";
  }
  const bmi = weight / (height / 100) ** 2;
  return (Math.round(bmi * 10) / 10).toFixed(1);
};

const parseAnthropometryAnamnesis = (raw) => {
  const parsed = parseJsonObject(raw, ANTHROPOMETRY_DEFAULT);
  return {
    weight_kg: parsed.weight_kg ?? "",
    height_cm: parsed.height_cm ?? "",
    body_type: ANTHROPOMETRY_BODY_TYPE_KEYS.includes(parsed.body_type) ? parsed.body_type : "",
    weight_fluctuation_3_5_years: parsed.weight_fluctuation_3_5_years ?? "",
    weight_fluctuation_1_year: parsed.weight_fluctuation_1_year ?? "",
    volume_redistribution: ANTHROPOMETRY_REDISTRIBUTION_KEYS.includes(parsed.volume_redistribution)
      ? parsed.volume_redistribution
      : "",
  };
};

const formatYesNoAnswer = (answer, t) => {
  if (answer === "yes") return t("anamnesis.labels.yes");
  if (answer === "no") return t("anamnesis.labels.no");
  return "—";
};

const formatGynRegularity = (value, t) => {
  if (value === "regular") return t("anamnesis.gyn.regular");
  if (value === "irregular") return t("anamnesis.gyn.irregular");
  return "—";
};

const formatMealRegime = (value, t) => {
  if (value === "regular") return t("anamnesis.nutrition.regular");
  if (value === "irregular") return t("anamnesis.nutrition.irregular");
  return "—";
};

const formatSports = (value, t) => {
  if (value === "yes") return t("anamnesis.routine.sportsYes");
  if (value === "no") return t("anamnesis.routine.sportsNo");
  return "—";
};

const formatAwakening = (awakening, awakeningTime, t) => {
  const time = String(awakeningTime ?? "").trim();
  const parts = normalizeCheckboxKeys(awakening, DAILY_ROUTINE_AWAKENING_KEYS).map((key) => {
    if (key !== "spontaneous") return t(DAILY_ROUTINE_AWAKENING_LABELS[key]);
    return time
      ? t("anamnesis.routine.wake.spontaneousAt", { time })
      : t(DAILY_ROUTINE_AWAKENING_LABELS.spontaneous);
  });
  return parts.length ? parts.join(", ") : "—";
};

const PrintField = ({ label, value, hideEmpty = false }) => {
  const text = formatAnyValue(value);
  if (hideEmpty && text === "—") return null;
  return (
    <div className="patient-profile-print-field">
      <strong>{label}:</strong> {text}
    </div>
  );
};

const PrintYesNoTable = ({ keys, labels, data, t }) => (
  <div className="patient-profile-print-yesno">
    <div className="patient-profile-print-yesno-header">
      <span className="patient-profile-print-yesno-label" />
      <span className="patient-profile-print-yesno-answer">
        {t("profile.print.answer")}
      </span>
      <span className="patient-profile-print-yesno-note">
        {t("profile.print.note")}
      </span>
    </div>
    {keys.map((key) => {
      const item = data?.[key] || YES_NO_ANAMNESIS_ITEM_DEFAULT;
      return (
        <div className="patient-profile-print-yesno-row" key={key}>
          <span className="patient-profile-print-yesno-label">
            {t(labels[key])}
          </span>
          <span className="patient-profile-print-yesno-answer">
            {formatYesNoAnswer(item.answer, t)}
          </span>
          <span className="patient-profile-print-yesno-note">{formatAnyValue(item.note)}</span>
        </div>
      );
    })}
  </div>
);

function PatientProfilePrint() {
  const { t } = useTranslation("patient");
  const { patientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [patient, setPatient] = useState(null);
  const [latestIndicator, setLatestIndicator] = useState(null);
  const [printTab, setPrintTab] = useState("classic");

  useEffect(() => {
    const fetchPatient = async () => {
      setLoading(true);
      setError("");
      try {
        const [response, indicatorsData] = await Promise.all([
          GetData({
            module: "patient",
            block: "info",
            id_patient: patientId,
          }),
          GetData({
            module: "patient",
            block: "indicators",
            id_patient: patientId,
          }),
        ]);
        setPatient(response?.result || null);
        setLatestIndicator(indicatorsData?.result?.[0] || null);
      } catch (err) {
        setError(err?.message || t("profile.print.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [patientId, t]);

  const fields = patient?.fields || {};

  const lifeAnamnesis = useMemo(
    () => parseLifeAnamnesis(fields?.life_anamnesis?.value),
    [fields?.life_anamnesis?.value],
  );
  const somaticDiseases = useMemo(
    () => parseSomaticDiseases(fields?.somatic_diseases?.value),
    [fields?.somatic_diseases?.value],
  );
  const gynAnamnesis = useMemo(
    () => parseGynAnamnesis(fields?.gyn_anamnesis?.value),
    [fields?.gyn_anamnesis?.value],
  );
  const allergyAnamnesis = useMemo(
    () => parseAllergyAnamnesis(fields?.allergy_anamnesis?.value),
    [fields?.allergy_anamnesis?.value],
  );
  const epidAnamnesis = useMemo(
    () => parseEpidAnamnesis(fields?.epid_anamnesis?.value),
    [fields?.epid_anamnesis?.value],
  );
  const nutritionAnamnesis = useMemo(
    () => parseNutritionAnamnesis(fields?.nutrition_anamnesis?.value),
    [fields?.nutrition_anamnesis?.value],
  );
  const dailyRoutine = useMemo(
    () => parseDailyRoutineAnamnesis(fields?.daily_routine_anamnesis?.value),
    [fields?.daily_routine_anamnesis?.value],
  );
  const anthropometry = useMemo(
    () => parseAnthropometryAnamnesis(fields?.anthropometry_anamnesis?.value),
    [fields?.anthropometry_anamnesis?.value],
  );

  if (loading) return <div className="profile-print-loading">{t("plan.loading")}</div>;
  if (error)
    return (
      <div className="profile-print-error">
        {t("usi.errorPrefix", { message: error })}
      </div>
    );
  if (!patient)
    return (
      <div className="profile-print-empty">
        {t("anamnesis.errors.patientNotFound")}
      </div>
    );

  const fioPatient = formatAnyValue(patient?.fio?.value);
  const fioDoctor = formatAnyValue(patient?.doc_name?.value?.name || "");
  const cardNumber = formatAnyValue(patient?.cart?.value);
  const birthDateRaw = patient?.birthDate?.value;
  const birthDate = formatDate(birthDateRaw);
  const ageYears = calcAgeYears(birthDateRaw);
  const ageText = ageYears == null ? "—" : String(ageYears);
  const treatmentStartDateRaw =
    patient?.date_start_treatment?.value ||
    patient?.date_start?.value ||
    patient?.date_created?.value ||
    "";
  const treatmentStartDate = treatmentStartDateRaw
    ? formatDateForDisplay(String(treatmentStartDateRaw).split(" ")[0])
    : "—";

  const foodCharacterText =
    nutritionAnamnesis.food_character
      .map((key) => t(NUTRITION_FOOD_CHARACTER_LABELS[key]))
      .filter(Boolean)
      .join(", ") || "—";

  const sleepCharacterText =
    dailyRoutine.sleep_character
      .map((key) => t(DAILY_ROUTINE_SLEEP_CHARACTER_LABELS[key]))
      .filter(Boolean)
      .join(", ") || "—";

  const physicalActivityText = dailyRoutine.physical_activity
    ? t(DAILY_ROUTINE_ACTIVITY_LABELS[dailyRoutine.physical_activity])
    : "—";

  const bodyTypeText = anthropometry.body_type
    ? t(ANTHROPOMETRY_BODY_TYPE_LABELS[anthropometry.body_type])
    : "—";

  const redistributionText = anthropometry.volume_redistribution
    ? t(ANTHROPOMETRY_REDISTRIBUTION_LABELS[anthropometry.volume_redistribution])
    : "—";

  const bmiText = calcBmi(anthropometry.weight_kg, anthropometry.height_cm) || "—";

  return (
    <div className="patient-profile-print-page">
      <div className="profile-print-controls no-print">
        <IconButton
          icon="print"
          onClick={() => window.print()}
          title={t("plan.buttons.print")}
          variant="secondary"
        />
        <IconButton
          icon="back"
          onClick={() => window.history.back()}
          title={t("plan.print.back")}
          variant="primary"
        />
      </div>

      <div className="profile-print-tabs no-print">
        <button
          type="button"
          className={`profile-print-tab${printTab === "classic" ? " is-active" : ""}`}
          onClick={() => setPrintTab("classic")}>
          {t("profile.print.tabClassic")}
        </button>
        <button
          type="button"
          className={`profile-print-tab${printTab === "form" ? " is-active" : ""}`}
          onClick={() => setPrintTab("form")}>
          {t("profile.print.tabForm")}
        </button>
      </div>

      <div
        className={`patient-profile-print-card${
          printTab === "form" ? " patient-profile-print-card--form" : ""
        }`}>
        {printTab !== "form" && (
          <>
            <div className="patient-plan-print-intro-upper">
              <div className="patient-plan-print-intro-upper-left">
                <div className="patient-plan-print-intro-title">
                  {t("profile.print.medCard")}
                </div>
              </div>
              <div className="patient-plan-print-intro-upper-right">
                <img
                  src={LogoText}
                  alt={t("profile.print.logoAlt")}
                  className="patient-plan-print-logo"
                />
              </div>
            </div>

            <div className="patient-plan-print-intro">
              <div className="patient-plan-print-intro-left">
                <div className="patient-plan-fio-block">
                  <div className="patient-plan-fio-img">
                    <img
                      src={userPlanPrint}
                      alt={t("profile.print.patientFio")}
                      className="patient-plan-print-intro-icon"
                    />
                  </div>
                  <div className="patient-plan-print-intro-line-block">
                    <div className="patient-plan-print-intro-line">
                      <strong>{t("plan.print.patient")}</strong>
                      <div className="patient-plan-print-patient-block">{fioPatient}</div>
                    </div>
                    <div className="patient-plan-print-intro-line date">
                      <strong>{t("profile.print.birthDate")}</strong>
                      <div className="patient-plan-print-patient-block">
                        {birthDate}
                        {ageYears != null
                          ? ` (${t("profile.print.age", { age: ageText })})`
                          : ""}                      </div>
                    </div>
                    <div className="patient-plan-print-intro-line date">
                      <strong>{t("plan.header.startDate")}</strong>
                      <div className="patient-plan-print-patient-block">{treatmentStartDate}</div>
                    </div>
                    <div className="patient-plan-print-intro-line date">
                      <strong>{t("profile.print.cardNumber")}</strong>
                      <div className="patient-plan-print-patient-block">{cardNumber}</div>
                    </div>
                  </div>
                </div>

                <div className="vertical-line"></div>

                <div className="patient-plan-doctor-block">
                  <div className="patient-plan-doctor-block-left">
                    <div className="patient-plan-doctor-img">
                      <img
                        src={doctorPlanPrint}
                        alt={t("plan.print.doctorAlt")}
                        className="patient-plan-print-intro-icon"
                      />
                    </div>
                    <div className="patient-plan-print-intro-line">
                      <strong>{t("plan.print.doctorFio")}</strong>
                      <div className="patient-plan-print-patient-block">{fioDoctor}</div>
                    </div>
                  </div>
                  <div className="patient-plan-doctor-block-right">
                    <div className="patient-plan-print-intro-line signature">
                      <strong>{t("plan.print.signature")}</strong>
                      <span className="patient-plan-print-signature-line" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {printTab === "form" ? (
          <PatientProfilePrintFormView
            complaints={fields?.complaints?.value}
            lifeAnamnesis={lifeAnamnesis}
            somaticDiseases={somaticDiseases}
            gynAnamnesis={gynAnamnesis}
            gynecologicalHistory={fields?.gynecological_history?.value}
            allergyAnamnesis={allergyAnamnesis}
            epidAnamnesis={epidAnamnesis}
            nutritionAnamnesis={nutritionAnamnesis}
            dailyRoutine={dailyRoutine}
            anthropometry={anthropometry}
            bmiText={bmiText === "—" ? "" : bmiText}
          />
        ) : (
          <>
        <section className="patient-profile-print-section">
          <h3>{t("anamnesis.sections.life")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintField
              label={t("anamnesis.labels.generalComplaints")}
              value={fields?.complaints?.value}
            />
            <PrintField
              label={t(LIFE_ANAMNESIS_FIELD_LABELS.general_condition)}
              value={lifeAnamnesis.general_condition}
            />
            <PrintField
              label={t(LIFE_ANAMNESIS_FIELD_LABELS.operations_injuries)}
              value={lifeAnamnesis.operations_injuries}
            />
            <PrintField
              label={t(LIFE_ANAMNESIS_FIELD_LABELS.medications)}
              value={lifeAnamnesis.medications}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("profile.print.sections.somaticGeneral")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintField
              label={t("anamnesis.somatic.chronicDiseases")}
              value={somaticDiseases.chronic_diseases}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("profile.print.sections.endocrine")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintField
              label={t("anamnesis.somatic.endocrineStatus")}
              value={somaticDiseases.endocrine_status}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("anamnesis.sections.gyn")}</h3>
          <div className="patient-profile-print-section-content">
            <div className="patient-profile-print-field">
              <strong>{t("anamnesis.gyn.menstruationFrom")}:</strong>{" "}
              {formatAnyValue(gynAnamnesis.menstruation_age)}{" "}
              {t("anamnesis.gyn.yearsShort")}{" "}
              {formatGynRegularity(gynAnamnesis.regularity, t)}
            </div>
            <PrintField
              label={t("anamnesis.gyn.cycleDuration")}
              value={gynAnamnesis.cycle_duration}
            />
            <PrintField label={t("anamnesis.gyn.pms")} value={gynAnamnesis.pms} />
            <div className="patient-profile-print-field">
              <strong>{t("anamnesis.gyn.pregnancyTotal")}</strong>{" "}
              {formatAnyValue(gynAnamnesis.pregnancy_r)}{" "}
              {t("anamnesis.gyn.pregnancyA")}{" "}
              {formatAnyValue(gynAnamnesis.pregnancy_a)}{" "}
              {t("anamnesis.gyn.pregnancyV")}{" "}
              {formatAnyValue(gynAnamnesis.pregnancy_v)})
            </div>
            <PrintField
              label={t("anamnesis.gyn.menstrualSymptoms")}
              value={gynAnamnesis.menstrual_symptoms}
            />
            <PrintField
              label={t("anamnesis.gyn.menopauseFrom")}
              value={gynAnamnesis.menopause_age}
            />
            <PrintField
              label={t("anamnesis.gyn.hormonalDrugs")}
              value={gynAnamnesis.hormonal_drugs}
            />
            <PrintField
              label={t("anamnesis.gyn.diseases")}
              value={fields?.gynecological_history?.value}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("anamnesis.sections.allergy")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintYesNoTable
              keys={ALLERGY_ANAMNESIS_KEYS}
              labels={ALLERGY_ANAMNESIS_FIELD_LABELS}
              data={allergyAnamnesis}
              t={t}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("anamnesis.sections.epid")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintYesNoTable
              keys={EPID_ANAMNESIS_KEYS}
              labels={EPID_ANAMNESIS_FIELD_LABELS}
              data={epidAnamnesis}
              t={t}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("anamnesis.sections.nutrition")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintField
              label={t("anamnesis.nutrition.mealRegime")}
              value={formatMealRegime(nutritionAnamnesis.meal_regime, t)}
            />
            <PrintField
              label={t("anamnesis.nutrition.mealsPerDay")}
              value={nutritionAnamnesis.meals_per_day}
            />
            <PrintField
              label={t("anamnesis.nutrition.foodCharacter")}
              value={foodCharacterText}
            />
            <PrintField
              label={t("anamnesis.nutrition.fluidLiters")}
              value={nutritionAnamnesis.fluid_liters}
            />
            <PrintField
              label={t("anamnesis.nutrition.smokingAlcohol")}
              value={nutritionAnamnesis.smoking_alcohol}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("anamnesis.sections.routine")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintField
              label={t("anamnesis.routine.sleepHours")}
              value={dailyRoutine.sleep_hours}
            />
            <PrintField
              label={t("anamnesis.routine.sleepCharacter")}
              value={sleepCharacterText}
            />
            <PrintField
              label={t("anamnesis.routine.awakening")}
              value={formatAwakening(
                dailyRoutine.awakening,
                dailyRoutine.awakening_time,
                t,
              )}
            />
            <PrintField
              label={t("anamnesis.routine.activityTitle")}
              value={physicalActivityText}
            />
            <PrintField
              label={t("anamnesis.routine.activityPeak")}
              value={dailyRoutine.activity_peak}
            />
            <PrintField
              label={t("anamnesis.routine.sports")}
              value={formatSports(dailyRoutine.sports, t)}
            />
            <PrintField
              label={t("anamnesis.routine.stressLevel")}
              value={dailyRoutine.stress_level}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("anamnesis.sections.anthropometry")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintField
              label={t("anamnesis.anthropometry.weightKg")}
              value={anthropometry.weight_kg}
            />
            <PrintField
              label={t("anamnesis.anthropometry.heightCm")}
              value={anthropometry.height_cm}
            />
            <PrintField
              label={t("anamnesis.anthropometry.bodyType")}
              value={bodyTypeText}
            />
            <PrintField
              label={t("anamnesis.anthropometry.fluctuation35")}
              value={anthropometry.weight_fluctuation_3_5_years}
            />
            <PrintField
              label={t("anamnesis.anthropometry.fluctuation1")}
              value={anthropometry.weight_fluctuation_1_year}
            />
            <PrintField
              label={t("anamnesis.anthropometry.bmi")}
              value={bmiText}
            />
            <PrintField
              label={t("anamnesis.anthropometry.redistribution")}
              value={redistributionText}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("profile.sections.indicators")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintField
              label={t("profile.fields.biologicalAge")}
              value={fields?.biological_age?.value}
            />
            <PrintField
              label={t("profile.fields.cvRisk")}
              value={fields?.cardiovascular_risk?.value}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("profile.sections.generalInfo")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintField label={t("profile.fields.cart")} value={patient?.cart?.value} />
            <PrintField
              label={t("profile.fields.doctor")}
              value={patient?.doc_name?.value?.name ?? patient?.doc_name?.value}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("profile.print.sections.diagnosisComplaints")}</h3>
          <div className="patient-profile-print-section-content">
            <PrintField
              label={t("profile.fields.diagnosis")}
              value={fields?.diagnosis?.value}
            />
            <PrintField
              label={t("profile.fields.complaints")}
              value={fields?.complaints?.value}
            />
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("profile.sections.diseaseAnamnesis")}</h3>
          <div className="patient-profile-print-section-content">
            {DISEASE_ANAMNESIS_KEYS.map((key) => (
              <PrintField
                key={key}
                label={t(DISEASE_ANAMNESIS_LABELS[key])}
                value={fields?.[key]?.value}
              />
            ))}
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("profile.print.sections.lifeExtra")}</h3>
          <div className="patient-profile-print-section-content">
            {OLD_LIFE_ANAMNESIS_KEYS.map((key) => (
              <PrintField
                key={key}
                label={t(OLD_LIFE_ANAMNESIS_LABELS[key])}
                value={fields?.[key]?.value}
              />
            ))}
          </div>
        </section>

        <section className="patient-profile-print-section">
          <h3>{t("profile.print.sections.examination")}</h3>
          {latestIndicator ? (
            <div className="patient-profile-print-section-content">
              <PrintField
                label={t("profile.print.visitDate")}
                value={latestIndicator?.date_created}
              />
              <PrintField
                label={t("profile.fields.bloodPressure")}
                value={latestIndicator?.blood_pressure}
              />
              <PrintField label={t("profile.print.weight")} value={latestIndicator?.weight} />
              <PrintField
                label={t("profile.fields.chestCircumference")}
                value={latestIndicator?.chest_circumference_cm}
              />
              <PrintField
                label={t("profile.fields.waistCircumference")}
                value={latestIndicator?.waist_circumference_cm}
              />
              <PrintField
                label={t("profile.fields.hipsCircumference")}
                value={latestIndicator?.hips_circumference_cm}
              />
              <PrintField
                label={t("profile.print.inspection")}
                value={latestIndicator?.inspection}
              />
            </div>
          ) : (
            <div className="patient-profile-print-section-content">
              {t("profile.print.noExaminationData")}
            </div>
          )}
        </section>

        <div className="patient-profile-print-consent">
          <p className="patient-profile-print-consent-text">
            {t("profile.print.consentLead")}{" "}
            <span className="patient-profile-print-consent-fio-line" />
            <br />
            {t("profile.print.consentBody")}
          </p>
          <div className="patient-profile-print-consent-signs">
            <div className="patient-profile-print-consent-sign">
              <span className="patient-profile-print-consent-sign-line" />
              <span className="patient-profile-print-consent-sign-label">
                {t("profile.print.patientSignature")}
              </span>
            </div>
            <div className="patient-profile-print-consent-sign">
              <span className="patient-profile-print-consent-sign-line patient-profile-print-consent-sign-line--wide" />
              <span className="patient-profile-print-consent-sign-label">
                {t("profile.print.signatureDecoding")}
              </span>
            </div>
            <div className="patient-profile-print-consent-sign">
              <span className="patient-profile-print-consent-date">
                {t("profile.print.datePlaceholder")}
              </span>
              <span className="patient-profile-print-consent-sign-label">
                {t("profile.print.dateLabel")}
              </span>
            </div>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PatientProfilePrint;
